/**
 * 搜索结果骨架屏组件
 *
 * CLS 优化：为 RecentFollow 搜索结果区域提供布局稳定的骨架屏
 *
 * 问题：原有 loading spinner 高度约 100px，而实际结果区域高度约 500px+，
 * 切换时造成约 400px 的布局位移，导致严重的 CLS 问题。
 *
 * 解决：使用与最终结果布局一致的骨架屏，保持容器高度稳定。
 *
 * 数据返回规则：
 * - 未登录/非付费用户：首次搜索返回 5 条
 * - 付费用户：首次搜索返回 45 条
 */
import { Skeleton } from 'antd';
import styles from '../index.module.less';
import classNames from 'classnames';
import { cn } from '@/lib/utils';
import { useTranslation } from 'next-i18next';

interface ResultsSkeletonProps {
  activeTab: 'follows' | 'followers';
  /** 是否为付费用户，决定骨架屏数量：付费 45 条，非付费 5 条 */
  isPremium?: boolean;
  /** 是否显示用户信息骨架屏（首次搜索时显示） */
  showUserInfoSkeleton?: boolean;
}

export default function ResultsSkeleton({
  activeTab,
  isPremium = false,
  showUserInfoSkeleton = false,
}: ResultsSkeletonProps) {
  const { t } = useTranslation();
  // 根据用户类型决定骨架屏数量
  const skeletonCount = isPremium ? 45 : 5;

  return (
    <>
      {/* 用户信息骨架屏 - 与 InstagramUserInfo 组件布局完全一致 */}
      {showUserInfoSkeleton && (
        <div className="flex justify-center items-center gap-4 mt-8 text-center max-md:flex-wrap max-md:justify-start max-md:items-start max-md:gap-2.5">
          {/* 头像 */}
          <div className="w-[4.25rem] h-[4.25rem] rounded-full overflow-hidden max-md:w-[2rem] max-md:h-[2rem] flex-shrink-0 bg-gray-200 animate-pulse" />
          <div className="flex gap-4 max-md:flex-1 max-md:block max-md:space-y-1.5">
            <div className="flex items-center gap-3 max-md:gap-2">
              {/* 全名 */}
              <div className="h-7 w-16 bg-gray-200 rounded animate-pulse max-md:h-5" />
              {/* ins图标 + @用户名 */}
              <div className="flex items-center gap-1 text-[#6b7280]">
                <div className="w-4 h-4 max-md:w-[1.375rem] max-md:h-[1.375rem] bg-gray-200 rounded animate-pulse" />
                <div className="h-4 w-20 bg-gray-200 rounded animate-pulse" />
              </div>
            </div>
            {/* 100% anonymous view 绿色标签 */}
            <div className="h-8 px-3 leading-8 rounded-full flex items-center gap-1 text-[0.875rem] text-[#0FA733] bg-[#D7EED7] max-md:w-max max-md:h-6 max-md:leading-6 max-md:text-[0.75rem]">
              <div className="w-4 h-4 overflow-hidden max-md:w-3 max-md:h-3 bg-[#0FA733]/30 rounded animate-pulse" />
              <span className="leading-4 font-medium">{t('100% anonymous view')}</span>
            </div>
          </div>
        </div>
      )}

      <div className={cn(styles.results, showUserInfoSkeleton && 'mt-8 max-md:mt-4')}>
        {/* Tabs - 真实渲染，保持与最终结果一致 */}
        <div className={styles.tabs}>
          <div className={classNames([styles.tab, activeTab === 'follows' && styles.active])}>
            {t('Recent Follows')}
          </div>
          <div className={classNames([styles.tab, activeTab === 'followers' && styles.active])}>
            {t('Recent Followers')}
          </div>
        </div>

        {/* Content - 骨架屏 */}
        <div className={cn(styles.content, 'p-8 max-md:p-4')}>
          {/* 标题骨架 - 与 h2(font-size: 1.25rem/20px) 高度一致 */}
          <div className="flex items-center gap-4 mb-4">
            <Skeleton.Input active style={{ width: 280, height: 24 }} />
          </div>

          {/* 用户列表骨架 - 根据用户类型显示不同数量 */}
          <div className={styles['user-list']} style={{ marginBottom: '20px' }}>
            {Array.from({ length: skeletonCount }, (_, i) => i + 1).map((i) => (
              <div key={i} className={styles.userItem} style={{ cursor: 'default' }}>
                {/* 排名 - 真实渲染 */}
                <span className={classNames([styles.rank, i <= 3 && styles['top-3'], 'flex-shrink-0'])}>{i}</span>
                {/* 头像骨架 - 与真实内容一致: 40x40 */}
                <Skeleton.Avatar active size={40} className="mr-4" />
                {/* 用户信息骨架 */}
                <div className={styles['user-info']}>
                  <Skeleton.Input active style={{ width: 120, height: 20, marginBottom: 4 }} />
                  <Skeleton.Input active style={{ width: 80, height: 16 }} />
                </div>
              </div>
            ))}
          </div>

          {/* 性别分布指示点骨架 - 与实际内容保持一致 */}
          <div className="mt-6 mb-8 flex justify-center items-center space-x-2 max-md:mt-[1.25rem] max-md:mb-7">
            <span className="w-2 h-2 bg-[#E0E0E0] rounded-full"></span>
            <span className="w-2 h-2 bg-[#E0E0E0] rounded-full"></span>
            <span className="w-2 h-2 bg-[#E0E0E0] rounded-full"></span>
          </div>

          {/* 性别分布骨架 - 与 GenderDistribution 组件高度一致 */}
          <div className="flex items-center justify-between px-2 text-left">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center justify-center gap-3">
                <Skeleton.Avatar active size={42} />
                <div className="flex flex-col gap-1">
                  <Skeleton.Input active style={{ width: 40, height: 16 }} />
                  <Skeleton.Input active style={{ width: 30, height: 20 }} />
                </div>
              </div>
            ))}
          </div>

          {/* 底部按钮骨架 */}
          <div className={styles['load-more']}>
            <Skeleton.Input active style={{ width: 280, height: 20, marginBottom: 16 }} />
            <Skeleton.Button active style={{ width: 312, height: 48 }} />
          </div>
        </div>
      </div>
    </>
  );
}
